// /services/eventBus.ts
import { EventEmitter } from 'events';

import { useAuthStore } from '@/store/authStore';
import { useChallengeStore } from '@/store/challengeStore';
import { useNostrMessageStore } from '@/store/nostrMessageStore';
import { useNoticeStore } from '@/store/noticeStore';
import { usePostStore } from '@/store/postStore';
import { useResponseStore } from '@/store/responseStore';
import { useWebSocketStore } from '@/store/webSocketStore';
import { Post } from '@/types/Post';
import { ChallengeSchema } from '@/types/Challenge';

export class DuellyEventEmitter extends EventEmitter {
    private hasRegistered = false;

    register() {
        if (this.hasRegistered) return;
        this.hasRegistered = true;

        setTimeout(() => {
            console.log(`📥 Registering global events`);
            this.on('login-success', () => {
                console.log('login-success');
                const nostrMessageStore = useNostrMessageStore();
                nostrMessageStore.generateAndStoreNostrKeys();
            });
            this.on('nostr_dm_sync', () => {
                console.log('Nostr Sync Event');
                const nostrMessageStore = useNostrMessageStore();
                nostrMessageStore.sync();
            });
            this.on('nostr_dm_received', (data) => {
                console.log('Event received:', data);
                const nostrMessageStore = useNostrMessageStore();
                nostrMessageStore.decryptAndStoreMessage(data.event_data, 'nostr_dm_received');
            });

            this.on('nostr_dm_thread', (data) => {
                console.log('Event received:', data);
                const nostrMessageStore = useNostrMessageStore();
                nostrMessageStore.decryptAndStoreMessage(data.event_data, 'nostr_dm_thread');
            });

            this.on('nostr_dm_inbox_preview', (data) => {
                console.log('Nost DM Inbox Preview Event received:', data);
                const nostrMessageStore = useNostrMessageStore();
                const event = data.event_data;
                nostrMessageStore.addInboxPreview(event);

                const thread = nostrMessageStore.threads[event.thread_key];
                const oldestTimestamp = thread?.messages?.[0]?.created_at || 0;

                const wsStore = useWebSocketStore();
                if (!wsStore.wsService) {
                    console.warn('WebSocket is not connected');
                    return;
                }
                const authStore = useAuthStore();
                wsStore.wsService.reqNostrEvents(`dm-thread-hydrate-${event.thread_key}`, {
                    name: 'send_dm_thread_history',
                    kinds: [4],
                    thread_key: event.thread_key,
                    user_id: authStore.getUserProfile().id,
                    since: 0,
                    limit: 50,
                });
            });

            this.on('new_notification', (data) => {
                const noticeStore = useNoticeStore();
                for (const notification of data.notifications) {
                    noticeStore.addNotification(notification);
                }
            });

            this.on('challenge_modified', (data) => {
                const challengeStore = useChallengeStore();

                // 1) parse the thin websocket payload as a partial
                const incoming = ChallengeSchema.partial().passthrough().parse(data.challenge);

                // 2) merge with what's in the store (if anything)
                const PK = incoming.PK?.startsWith?.('CHALLENGE#')
                    ? incoming.PK
                    : `CHALLENGE#${incoming.PK}`;
                const prev = challengeStore.getChallenge(PK);

                // 3) run a full parse on the merged object to reapply defaults (e.g., objectFit)
                const normalized = ChallengeSchema.passthrough().parse({
                    ...(prev ?? {}),
                    ...incoming,
                });

                // 4) store via your normal add path (shallow replace is fine now)
                challengeStore.add([normalized]);
            });

            this.on('responseModified', (data) => {
                const responseStore = useResponseStore();
                responseStore.add([data.response]);
            });

            this.on('postModified', (data) => {
                console.log('postModified', data);
                const postStore = usePostStore();
                postStore.update(data.post.PK, data.post);
            });

            this.on('postVideoProcessed', (data) => {
                console.log('postVideoProcessed', data);
                const postStore = usePostStore();
                postStore.update(data.post.PK, data.post);
            });
        }, 100);
    }

    unregisgter() {
        this.removeAllListeners();
        this.hasRegistered = false;
    }
}

export const duellyEventEmitter = new DuellyEventEmitter();
duellyEventEmitter.register();

// const originalEmit = duellyEventEmitter.emit;
// duellyEventEmitter.emit = function (event: string, ...args: any[]) {
//     console.log('[EventBus] 🔔 Event emitted:', event, ...args);
//     return originalEmit.call(this, event, ...args);
// };
